import * as Sentry from "@sentry/nextjs";
import { useAtomValue, useSetAtom } from "jotai";
import qs from "qs";
import { useCallback, useEffect, useState } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { getGoogleAnalyticsClientId } from "shared/components/tracking/google-analytics/apis";
import { useLighthouseMapValue } from "shared/components/tracking/mirror";
import { accountAtom } from "shared/lib/account";

import { useUserRoleTarget } from "scmp-app/components/advertisement/ad-slots/hooks";
import { isInfiniteScrollContentAtom } from "scmp-app/components/article/article-list/atoms";
import { onlyOnePlayingPlayerAtom } from "scmp-app/components/article/article-list/hooks";
import type { TrackingData } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/hooks/types";
import type { VideoOrientation } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/types";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { VideoPlayerAdEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import { appAtom } from "scmp-app/lib/app/atoms";

type AdData = {
  adInfo?: ADInfo;
  iu: string;
};

type Parameters = {
  adData: AdData;
  onInitialized?: () => void;
  orientation?: VideoOrientation;
  player?: YouTubePlayer;
  trackingData: TrackingData;
};

export const useImaIntegration = (parameters: Parameters) => {
  const { adData, onInitialized, orientation, player, trackingData } = parameters;
  const { user } = useAtomValue(accountAtom);
  const { navigatorInfo } = useAtomValue(appAtom);
  const { value: lighthouseMap } = useLighthouseMapValue();
  const userRoleTarget = useUserRoleTarget();
  const isInfiniteScrollContent = useAtomValue(isInfiniteScrollContentAtom);
  const setOnlyOnePlayingPlayer = useSetAtom(onlyOnePlayingPlayerAtom);

  const [adsManager, setAdsManager] = useState<google.ima.AdsManager>();
  const [adsLoader, setAdsLoader] = useState<google.ima.AdsLoader>();
  const [isAdStarted, setIsAdStarted] = useState(false);
  const [isAdPlaying, setIsAdPlaying] = useState(false);

  const handleAdGA4Tracking = (customParameters: VideoPlayerAdEventCustomizedParameters) => {
    if (customParameters.article_id === undefined) delete customParameters.article_id;

    sendGA4Tracking({
      action: "sys",
      category: "video",
      customized_parameters: customParameters,
      subcategory: "ad",
    });
  };

  const handleOnAdStarted = useCallback(
    (event: google.ima.AdEvent) => {
      setIsAdStarted(true);
      setIsAdPlaying(true);

      const ad = event.getAd();
      if (!ad) return;
      const adId = ad.getAdId();
      const adTimeOffset = ad.getAdPodInfo().getTimeOffset();

      handleAdGA4Tracking({
        ad_line_item_id: adId,
        article_id: trackingData.ga4ArticleEntityId,
        player_name: "youtube_pfp",
        position: trackingData.ga4Position ?? "article_body",
        time: adTimeOffset,
        video_id: trackingData.videoEntityId,
        youtube_id: trackingData.youTubeVideoId,
      });
    },
    [
      trackingData.ga4ArticleEntityId,
      trackingData.ga4Position,
      trackingData.videoEntityId,
      trackingData.youTubeVideoId,
    ],
  );

  const initImaManager = useCallback(
    async (player: YouTubePlayer, elementId: string, adElementId: string) => {
      if (!player) return;

      const gaClientId = await getGoogleAnalyticsClientId();

      const getCustomizedParameter = () => {
        const customizedParameter: Record<string, number | string | undefined | unknown[]> = {
          overlay_short: "false",
          ...adData.adInfo?.targeting,
          ...userRoleTarget,
          ...(orientation ? { v_video: orientation === "portrait" ? "true" : "false" } : {}),
          ad_bot: String(navigatorInfo?.isBot ?? false),
          bot_score: navigatorInfo?.botScoreGroup ?? "0",
          ga_id: gaClientId,
          infinite_scroll: String(isInfiniteScrollContent ?? false),
          uuid: user?.uuid,
        };
        return qs.stringify(
          Object.entries(customizedParameter).reduce(
            (accumulator, [key, value]) => {
              if (!value) return accumulator;
              accumulator[key] = Array.isArray(value) ? value.join(",") : `${value}`;
              return accumulator;
            },
            {
              ...lighthouseMap,
            } as Record<string, string>,
          ),
        );
      };

      const makeAdsRequest = (adsRequest: google.ima.AdsRequest) => {
        // cspell: ignore instream gdfp tfcd unviewed vpmute
        const adUrl = "https://pubads.g.doubleclick.net/gampad/ads";
        adsRequest.adTagUrl = `${adUrl}?${qs.stringify({
          correlator: "",
          cust_params: getCustomizedParameter(),
          env: "instream",
          gdfp_req: "1",
          impl: "s",
          iu: adData.iu.startsWith("/") ? adData.iu : `/${adData.iu}`,
          npa: "0",
          output: "vast",
          sz:
            orientation === "portrait"
              ? "400x300|480x70|480x360|480x361|640x480|360x480|361x480|480x640|360x640"
              : "400x300|480x70|480x360|480x361|640x480",
          tfcd: "0",
          unviewed_position_start: "1",
          url: window.location.origin,
          vpa: "click",
          vpmute: "1",
          wta: "1",
        })}`;
      };

      const handleConfigImaSdkSettings = () => {
        const imaSdkSettings = google.ima.settings;
        if (!imaSdkSettings) return;

        if (user?.uuid) {
          imaSdkSettings.setPpid(user.uuid);
          return;
        }

        if (gaClientId) imaSdkSettings.setPpid(gaClientId);
      };

      try {
        const imaManager = new YT.ImaManager(player, elementId, adElementId, makeAdsRequest);
        handleConfigImaSdkSettings();

        const adsLoader = imaManager.getAdsLoader();
        if (!adsLoader) {
          onInitialized?.();
          return;
        }
        setAdsLoader(adsLoader);
      } catch (error) {
        console.error("Error in IMA Initialization", error);
        onInitialized?.();
      }
    },
    [
      adData.adInfo?.targeting,
      adData.iu,
      isInfiniteScrollContent,
      lighthouseMap,
      navigatorInfo?.botScoreGroup,
      navigatorInfo?.isBot,
      onInitialized,
      orientation,
      user,
      userRoleTarget,
    ],
  );

  useEffect(() => {
    if (!adsLoader) return;

    const handleLoaded = async (event: google.ima.AdsManagerLoadedEvent) => {
      if (!player) return;
      onInitialized?.();

      const currentTime = await player.getCurrentTime();
      const duration = await player.getDuration();
      const adsManager = event.getAdsManager({
        currentTime,
        duration,
      });

      setAdsManager(adsManager);
      setOnlyOnePlayingPlayer(state => ({
        ...state,
        adsManager,
      }));
    };

    const handleError = (event: google.ima.AdErrorEvent) => {
      Sentry.captureException(event.getError().getMessage());
      onInitialized?.();
    };

    adsLoader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      handleLoaded,
    );
    adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleError);

    return () => {
      adsLoader.removeEventListener(
        google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        handleLoaded,
      );
      adsLoader.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleError);
    };
  }, [adsLoader, onInitialized, player, setOnlyOnePlayingPlayer]);

  useEffect(() => {
    if (!adsManager) return;

    const handleAdSkipped = () => {
      setIsAdStarted(false);
      setIsAdPlaying(false);
    };

    const handlePaused = () => {
      setIsAdPlaying(false);
    };

    const handleResumed = () => {
      setIsAdPlaying(true);
    };

    adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
    adsManager.addEventListener(google.ima.AdEvent.Type.PAUSED, handlePaused);
    adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, handleResumed);
    adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleAdSkipped);
    adsManager.addEventListener(google.ima.AdEvent.Type.SKIPPED, handleAdSkipped);
    adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, handleAdSkipped);

    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
      adsManager.removeEventListener(google.ima.AdEvent.Type.PAUSED, handlePaused);
      adsManager.removeEventListener(google.ima.AdEvent.Type.RESUMED, handleResumed);
      adsManager.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleAdSkipped);
      adsManager.removeEventListener(google.ima.AdEvent.Type.SKIPPED, handleAdSkipped);
      adsManager.removeEventListener(google.ima.AdEvent.Type.COMPLETE, handleAdSkipped);
    };
  }, [adsManager, handleOnAdStarted]);

  return {
    adsManager,
    initImaManager,
    isAdPlaying,
    isAdStarted,
  };
};
